import React, { memo, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { reduxForm } from "redux-form";
import { createStructuredSelector } from "reselect";
import validate, { asyncValidate } from "validations";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import {
  forms,
  AUTH_MODES,
  ERROR_CODES,
  paths,
  SIM_TYPES,
  CONTRACT_TYPES,
  CONFLICTING_ORDERS_STATUS
} from "utils/constants";
import { getDeviceId, removeTimerKey } from "utils/localStorage";
import {
  makeSelectPageValidations,
  makeSelectMonthOptions,
  makeSelectDateOptions,
  makeSelectYearOptions,
  makeSelectMinimumAge,
  makeSelectUserDetailsConfirmation,
  makeSelectDeliveryMethods,
  makeSelectUserDetailsPayload,
  makeSelectParentalConsentPayloadSelector,
  makeSelectPortinExpiryDateOptions,
  makeSelectUserDetailsInitialValues,
  makeSelectUserDetailsKycPayload,
  makeSelectUserDetailsOrderUpdatePayload,
  makeSelectDeliveryDetailsItems,
  makeSelectDeliveryDates,
  makeSelectCombinedDeliveryDates,
  makeSelectCvsDeliveryDates,
  makeSelectFreeDeliverySlots,
  makeSelectSpecialDeliverySlots,
  makeSelectPostOfficeRegions,
  makeSelectPostOfficeDeliverySlots,
  makeSelectSelectedPostOffice,
  makeSelectIsUploadIDLater,
  makeSelectPostalCodeValidation,
  makeSelectAddressComponentMappings,
  makeSelectCityList,
  makeSelectDistrictList,
  makeSelectStreetList,
  makeSelectStoreList,
  makeSelectGuestOrder,
  makeSelectIsFetchingData,
  makeSelectLiquidOcrData,
  makeSelectConnectionDetailsForm,
  makeSelectIsPortingUser,
  makeSelectIsPortingUserWithPortingNumber,
  makeSelectConsent,
  makeSelectIsModalView,
  makeSelectOrderParams,
  makeSelectOrderRef,
  makeSelectMaxAgeParentalConsent,
  makeSelectModalType,
  makeSelectThemeColors,
  makeSelectAuthId,
  makeSelectPortInUpdatePayload,
  makeSelectServiceInstanceNumber,
  makeSelectIsReInitiatePortIn
} from "ducks/selectors";
import {
  changeFormField,
  initializeForm,
  uploadSignature,
  getLiquidOcrData,
  registerOcrData,
  updateOrder,
  fetchAddressLookup,
  fetchDeliverySlots,
  resetDeliverySlots,
  fetchPostOfficeDeliverySlots,
  fetchAddressComponentMappings,
  findAddressLookup,
  validatePostalCode,
  resetPostalCodeValidation,
  setPostalCodeValidation,
  fetchCityList,
  fetchDistrictList,
  fetchStreetList,
  fetchStoreList,
  resetDistrictList,
  resetStreetList,
  resetStoreList,
  fetchUserOrders,
  setData,
  submitUserKycDetails,
  createInitialOrder,
  clearData,
  userLoginOTP,
  updateParentalConsent,
  updatePortInDetails,
  reInitiatePortIn
} from "ducks/actions";
import { buttonThemes } from "ui-components/theme";
import SmartRender from "containers/SmartRender";
import Spinner, { styles } from "components/Spinner";
import {
  gotoUrl,
  getChannelFromUrl,
  isMobile,
  pathWithChannel,
  portinProviders,
  sortAndFilterOrders
} from "utils/helpers";
import OTPModal from "components/OTPModal";
const OTP_MODAL_TYPE = "otpModal";

const UserDetailsConfirmation = Component =>
  function Comp(props) {
    const {
      getLiquidOcrData,
      fetchUserOrders,
      order,
      liquidOcrData,
      isFetchingOcrData,
      isRegisteringOCR,
      isUpdatingOrder,
      initialValues,
      initializeForm,
      isPortingUser,
      isSavingKycDetails,
      clearOrder,
      orderRef,
      isReInitiatePortIn,
      hideModal,
      themeColors,
      requestParentEmailOTP,
      submitUserDetailsWithParentalConsent,
      parentalConsentPayload,
      isOTPModalOpen: showOTPModal
    } = props;
    const ocrDataLoaded = get(initialValues, "ocrDataLoaded");
    const kycDataLoaded = get(initialValues, "kycDataLoaded");
    const loading = isSavingKycDetails || isUpdatingOrder;
    const buttonTheme = get(themeColors, "button", buttonThemes.povodarkgray);

    useEffect(() => {
      if (!isPortingUser) {
        if (
          orderRef &&
          (isEmpty(liquidOcrData) || liquidOcrData?.orderRef !== orderRef) &&
          !isReInitiatePortIn
        ) {
          getLiquidOcrData(orderRef);
        }
        if (isEmpty(order)) {
          fetchUserOrders();
        }
      }
      // eslint-disable-next-line
    }, []);

    useEffect(() => {
      if (ocrDataLoaded || kycDataLoaded) {
        initializeForm(initialValues);
      }
      // eslint-disable-next-line
    }, [ocrDataLoaded, kycDataLoaded]);

    useEffect(
      () => {
        if (!isEmpty(order?.error)) {
          clearOrder();
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [order?.error]
    );

    return !isPortingUser &&
      ((isEmpty(liquidOcrData) && !isReInitiatePortIn) ||
        isFetchingOcrData ||
        isRegisteringOCR ||
        isUpdatingOrder) ? (
      <Spinner size={200} sx={styles.fullPage} />
    ) : (
      <>
        <Component loading={loading} {...props} />
        <OTPModal
          modal_title="verify_parent_guardian_email"
          modal_subtitle="verify_account_by_otp_instructions"
          showModal={showOTPModal}
          showCloseButton={false}
          closeModal={hideModal}
          buttonTheme={buttonTheme}
          hideOtpResend={true}
          enableBackButton={true}
          info_message={"otp_help_faq"}
          headerStyles={{
            alignItems: "center",
            textAlign: "center",
            padding: "0.5rem",
            width: "100%",
            marginBottom: "2rem",
            overflowWrap: "anywhere"
          }}
          headerValues={{
            email: parentalConsentPayload.email
          }}
          otpBoxes={{
            size: 6,
            onSubmit: submitUserDetailsWithParentalConsent,
            buttonColor: buttonTheme,
            instruction: "otp_code",
            label: "continue",
            timerKey: "parent_otp",
            buttonWidth: ["100%", "60%"],
            timer: {
              label: "expiring_in",
              durationInSeconds: 120,
              enableResendLinkInSeconds: 60,
              resendLinkCallback: null
            }
          }}
          resendOTPLink={{
            resend: requestParentEmailOTP,
            isDisable: false
          }}
        />
      </>
    );
  };

const mapStateToProps = createStructuredSelector({
  formValidations: makeSelectPageValidations(
    forms.USER_DETAILS_CONFIRMATION_FORM
  ),
  formValues: makeSelectUserDetailsConfirmation(),
  order: makeSelectGuestOrder(),
  monthOptions: makeSelectMonthOptions(),
  dateOptions: makeSelectDateOptions(),
  yearOptions: makeSelectYearOptions(),
  minimumAge: makeSelectMinimumAge(),
  portinExpiryDateOptions: makeSelectPortinExpiryDateOptions(),
  payload: makeSelectUserDetailsPayload(),
  parentalConsentPayload: makeSelectParentalConsentPayloadSelector(),
  kycPayload: makeSelectUserDetailsKycPayload(),
  orderUpdatePayload: makeSelectUserDetailsOrderUpdatePayload(),
  deliveryMethods: makeSelectDeliveryMethods(),
  initialValues: makeSelectUserDetailsInitialValues(),
  deliveryDetailsItems: makeSelectDeliveryDetailsItems(),
  deliverySlotDates: makeSelectDeliveryDates(),
  combinedDeliverySlotDates: makeSelectCombinedDeliveryDates(),
  cvsDeliverySlotDates: makeSelectCvsDeliveryDates(),
  freeDeliverySlotTime: makeSelectFreeDeliverySlots(),
  specialDeliverySlotTime: makeSelectSpecialDeliverySlots(),
  postOfficeRegions: makeSelectPostOfficeRegions(),
  postOfficeDeliverySlots: makeSelectPostOfficeDeliverySlots(),
  selectedPostOffice: makeSelectSelectedPostOffice(),
  uploadIDLater: makeSelectIsUploadIDLater(),
  postalCodeValidation: makeSelectPostalCodeValidation(),
  addressComponentMappings: makeSelectAddressComponentMappings(),
  cityList: makeSelectCityList(),
  districtList: makeSelectDistrictList(),
  streetList: makeSelectStreetList(),
  storeList: makeSelectStoreList(),
  liquidOcrData: makeSelectLiquidOcrData(),
  isFetchingOcrData: makeSelectIsFetchingData("liquidOcrData"),
  isRegisteringOCR: makeSelectIsFetchingData("liquidOCRRegister"),
  isUpdatingOrder: makeSelectIsFetchingData("order"),
  isPortingUser: makeSelectIsPortingUser(),
  isPortingUserWithPortingNumber: makeSelectIsPortingUserWithPortingNumber(),
  connectionDetails: makeSelectConnectionDetailsForm(),
  isConsentReviewed: makeSelectConsent(),
  isModalView: makeSelectIsModalView(),
  modalType: makeSelectModalType(),
  orderParams: makeSelectOrderParams(),
  orderRef: makeSelectOrderRef(),
  isSavingKycDetails: makeSelectIsFetchingData("submitUserKycDetails"),
  isFetchingDeliverySlots: makeSelectIsFetchingData("deliverySlots"),
  maxAgeParentalConsent: makeSelectMaxAgeParentalConsent(),
  themeColors: makeSelectThemeColors(),
  authId: makeSelectAuthId(),
  updatePortInPayload: makeSelectPortInUpdatePayload(),
  accountNumber: makeSelectServiceInstanceNumber(),
  isReInitiatePortIn: makeSelectIsReInitiatePortIn()
});

export const mapDispatchToProps = dispatch => {
  return {
    clearOrder: () => {
      dispatch(clearData("order"));
    },
    uploadSignature: payload => dispatch(uploadSignature(payload)),
    changeField: (field, value) => {
      dispatch(
        changeFormField({
          form: forms.USER_DETAILS_CONFIRMATION_FORM,
          field: field,
          value: value
        })
      );
    },
    initializeForm: initialValues => {
      dispatch(
        initializeForm({
          form: forms.USER_DETAILS_CONFIRMATION_FORM,
          data: initialValues
        })
      );
      dispatch(
        setData({
          key: "isConsentReviewed",
          data: { isConsentReviewed: false }
        })
      );
      dispatch(setData({ key: "isModalView", data: { isModalView: true } }));
    },
    consentReviewed: () => {
      dispatch(
        setData({ key: "isConsentReviewed", data: { isConsentReviewed: true } })
      );
    },
    consentReviewedFalse: () => {
      dispatch(
        setData({
          key: "isConsentReviewed",
          data: { isConsentReviewed: false }
        })
      );
    },
    hideModal: () => {
      dispatch(
        setData({ key: "isModalView", data: { isModalView: false, type: "" } })
      );
      dispatch(clearData(["parentalConsentOtp"]));
    },
    updateOrder: (payload, callback) =>
      dispatch(updateOrder(payload, callback)),
    getLiquidOcrData: (orderRef, callback) => {
      dispatch(getLiquidOcrData(orderRef, callback));
    },
    registerOcrData: (payload, callback) => {
      dispatch(registerOcrData(payload, callback));
    },
    fetchPostOfficeDeliverySlots: params => {
      dispatch(fetchPostOfficeDeliverySlots(params));
    },
    fetchDeliverySlots: (params, callback) => {
      dispatch(fetchDeliverySlots(params, callback));
    },
    resetDeliverySlots: () => dispatch(resetDeliverySlots()),
    fetchAddressLookup: params => {
      dispatch(fetchAddressLookup(params));
    },
    fetchAddressComponentMappings: params => {
      dispatch(fetchAddressComponentMappings(params));
    },
    findAddressLookup: (params, callback) => {
      dispatch(findAddressLookup(params, callback));
    },
    validatePostalCode: (postal_code, field) => {
      dispatch(validatePostalCode({ postal_code }, field));
    },
    resetPostalCodeValidation: field => {
      dispatch(resetPostalCodeValidation(field));
    },
    setPostalCodeValidation: (field, data, loading) => {
      dispatch(setPostalCodeValidation(field, data, loading));
    },
    fetchCityList: params => {
      dispatch(fetchCityList(params));
    },
    fetchDistrictList: params => {
      dispatch(fetchDistrictList(params));
    },
    fetchStreetList: params => {
      dispatch(fetchStreetList(params));
    },
    fetchStoreList: params => {
      dispatch(fetchStoreList(params));
    },
    resetDistrictList: () => {
      dispatch(resetDistrictList());
    },
    resetStreetList: () => {
      dispatch(resetStreetList());
    },
    resetStoreList: () => {
      dispatch(resetStoreList());
    },
    fetchUserOrders: () => {
      dispatch(fetchUserOrders({}));
    },
    updateUserKYCDetails: (orderRef, kycPayload, navigate) => {
      const providers = portinProviders();
      const types = providers.map(provider => `${provider}_PORTIN`);
      const updateKycPayload = {
        kyc_identity: {
          provider: providers?.join(",") || "",
          kyc_type: types?.join(",") || "",
          identifier: orderRef
        },
        user_kyc: {
          ...kycPayload,
          order_id: orderRef
        }
      };
      dispatch(
        submitUserKycDetails(updateKycPayload, (_, error, status) => {
          if (isEmpty(error) || [409, 404].includes(status)) {
            navigate();
          } else {
            const { code } = _;

            if (code === ERROR_CODES.missingResourceForSubmitKycRequest) {
              gotoUrl(
                paths.CONNECTION_DETAILS.replace(
                  ":channel",
                  getChannelFromUrl()
                )
              );
            }
          }
        })
      );
    },
    createInitialOrder: (orderParams, callback) => {
      dispatch(createInitialOrder(orderParams, callback));
    },
    // Request OTP authentication for the parent email provided
    makeParentEmailOTP: bodyParams => {
      const headers = {};
      dispatch(
        userLoginOTP(headers, bodyParams, (_, errors) => {
          if (!errors) {
            dispatch(
              setData({
                key: "isModalView",
                data: { isModalView: true, type: OTP_MODAL_TYPE }
              })
            );
          }
        })
      );
    },
    // Submit User provided OTP. On Success user will be taken to next step
    submitMinorsDetails: (bodyParams, successCallback) => {
      const headers = {};
      dispatch(
        updateParentalConsent(headers, bodyParams, (response, errors) => {
          if (isEmpty(errors)) {
            dispatch(
              setData({
                key: "isModalView",
                data: { isModalView: false, type: "" }
              })
            );
            successCallback();
          } else {
            const { message } = response;
            dispatch(
              setData({
                key: "parentalConsentOtp",
                data: { errorMessage: message }
              })
            );
          }
        })
      );
    },
    updatePortInDetails: (updatePortInPayload, callback) => {
      dispatch(updatePortInDetails(updatePortInPayload, callback));
    },
    reInitiatePortIn: (payload, callback) => {
      dispatch(reInitiatePortIn(payload, callback));
    },
    fetchUserOrdersAndNavigateToManageOrder: () => {
      dispatch(
        fetchUserOrders({}, orders => {
          const filteredOrders = sortAndFilterOrders(orders);
          if (!isEmpty(filteredOrders)) {
            gotoUrl(paths.MANAGE_ORDER, true, {
              reset: null,
              iccid: null
            });
          }
        })
      );
    }
  };
};

export const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  isOTPModalOpen:
    stateProps.isModalView && stateProps.modalType === OTP_MODAL_TYPE,
  registerOcrDataAndNavigate: navigate => {
    dispatchProps.registerOcrData(stateProps.kycPayload, (_, error, status) => {
      if (isEmpty(error) || [409, 404].includes(status)) {
        navigate();
      }
    });
  },
  updateOrderAndNavigate: navigate => {
    dispatchProps.updateOrder(stateProps.orderUpdatePayload, (_, error) => {
      if (isEmpty(error)) {
        navigate();
      }
    });
  },
  registerOcrDataAndUpdateOrderAndNavigate: navigate => {
    dispatchProps.updateOrder(stateProps.orderUpdatePayload, (_, error) => {
      if (isEmpty(error)) {
        dispatchProps.registerOcrData(
          stateProps.kycPayload,
          (_, error, status) => {
            if (isEmpty(error) || [409, 404].includes(status)) {
              navigate();
            }
          }
        );
      }
    });
  },
  createInitialOrderAndUpdateKycDetailsAndNavigate: navigate => {
    const { orderParams, kycPayload } = stateProps;
    dispatchProps.createInitialOrder(orderParams, (order, error) => {
      const { status } = error || {};
      if (isEmpty(error)) {
        const { order_ref } = order;
        dispatchProps.updateUserKYCDetails(order_ref, kycPayload, navigate);
      } else if (status === CONFLICTING_ORDERS_STATUS) {
        dispatchProps.fetchUserOrdersAndNavigateToManageOrder();
      }
    });
  },
  requestParentEmailOTP: timerKey => {
    const email = stateProps?.formValues?.parent_email;
    const bodyParams = {
      email,
      auth_mode: AUTH_MODES.ENHANCED_EMAIL_OTP
    };
    dispatchProps.makeParentEmailOTP(bodyParams);
    timerKey && removeTimerKey(timerKey);
  },
  submitUserDetailsWithParentalConsent: (_, { current_otp }) => {
    const formValues = stateProps?.formValues || {};
    const otpCode = current_otp || formValues.otp || "";
    const authId = stateProps?.authId;
    const parentDetails = stateProps.parentalConsentPayload;
    const simType =
      stateProps?.order?.simType || stateProps?.connectionDetails?.sim_type;
    const contractType =
      stateProps?.order?.contractType ||
      stateProps?.connectionDetails?.number_type;
    const orderRef = stateProps?.orderRef;
    const dob = stateProps?.formValues?.dob || "";

    const bodyParams = {
      order_ref: orderRef,
      dob: dob,
      otp: {
        otp_code: otpCode,
        auth_id: authId,
        auth_mode: AUTH_MODES.ENHANCED_EMAIL_OTP,
        device: {
          app_type: "ecosystem",
          device_id: getDeviceId(),
          device_type: isMobile ? "Mobile" : "web"
        }
      },
      parent: parentDetails
    };
    dispatchProps.submitMinorsDetails(bodyParams, () => {
      if (stateProps.isPortingUserWithPortingNumber) {
        const nextUrl =
          simType === SIM_TYPES.PHYSICAL_SIM
            ? pathWithChannel(paths.DELIVERY_DETAILS)
            : pathWithChannel(paths.ORDER_SUMMARY);
        return gotoUrl(nextUrl, false, {});
      } else {
        if (contractType === CONTRACT_TYPES.PORT_IN)
          return gotoUrl(pathWithChannel(paths.PORTIN_DETAILS), false, {});
        if (simType === SIM_TYPES.PHYSICAL_SIM)
          return gotoUrl(pathWithChannel(paths.DELIVERY_DETAILS), false, {});
        else if (simType === SIM_TYPES.E_SIM) {
          dispatchProps.updateOrder(
            stateProps.orderUpdatePayload,
            (_, error) => {
              if (isEmpty(error)) {
                dispatchProps.registerOcrData(
                  stateProps.kycPayload,
                  (_, error, status) => {
                    if (isEmpty(error) || [409, 404].includes(status)) {
                      gotoUrl(paths.MANAGE_ORDER, false, {});
                    }
                  }
                );
              }
            }
          );
        }
      }
    });
  },
  updatePortInDetailsAndNavigate: navigate => {
    dispatchProps.updatePortInDetails(
      stateProps.updatePortInPayload,
      (_, error) => {
        if (isEmpty(error)) {
          navigate();
        }
      }
    );
  },
  reInitiatePortInAndNavigate: navigate => {
    const payload = {
      account_number: stateProps.accountNumber,
      donor_ref_id: get(stateProps.formValues, "mnp_reservation_number"),
      source: "ECOM"
    };
    dispatchProps.reInitiatePortIn(payload, (_, error) => {
      if (isEmpty(error)) {
        navigate();
      }
    });
  }
});

const withConnect = connect(mapStateToProps, mapDispatchToProps, mergeProps);
export default compose(
  withConnect,
  reduxForm({
    form: forms.USER_DETAILS_CONFIRMATION_FORM,
    destroyOnUnmount: false,
    validate,
    asyncValidate
  }),
  memo
)(UserDetailsConfirmation(SmartRender));
